import assertNever from "assert-never"
import React from "react"
import styled from "styled-components"

import {
  API,
  isSocketPlaygroundState,
  PlaygroundState,
  SocketConnectionState
} from "../reducer/common.types"
import { ConnectionButton, device, RegularText } from "../styles"
import { Card } from "./Card"

const LogoContainer = styled.div`
  grid-area: header-logo;
  display: flex;
  align-items: center;
  justify-content: space-evenly;

  @media ${device.smallerThen.tabletS} {
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
  }
`
const ConnectionContainer = styled(Card)`
  grid-area: header-connection;

  justify-content: space-evenly;
  display: flex;
  flex-wrap: wrap;

  align-items: center;
  font-size: 125%;
`

const getConnectionButtonText = (state: SocketConnectionState): string => {
  switch (state) {
    case "connected":
      return "Disconnect"
    case "connecting":
    case "disconnecting":
      return "Pending..."
    case "disconnected":
      return "Connect"
    case "subscribed":
      return "Cancel subscription"
    default:
      return assertNever(state)
  }
}

const P = styled(RegularText)`
  margin: 0;
`

const HeaderText = styled.h3`
  color: white;
  font-style: italic;
  margin: 0;
  text-align: center;
`

interface HeaderProps {
  state: PlaygroundState<API>
  /** Callback that gets invoked when the selected API changes.
   * This is triggered when the user selects a value from the
   * API selector dropdown.
   */
  onAPIChange: (event: React.ChangeEvent<HTMLSelectElement>) => any

  /**
   * Callback that gets invoked when the user has selected
   * a websocket based API, and clicks the "connect" button.
   * This callback then needs to dispatch some websocket
   * messages, and handle the changes in connection state.
   */
  onConnectionButtonClick: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => any
}

/**
 * The header of our application. This includes a clickable
 * Suredbits logo and the API selector.
 */
export const Header: React.SFC<HeaderProps> = ({
  state,
  onAPIChange,
  onConnectionButtonClick
}) => {
  const isSelectDisabled = isSocketPlaygroundState(state)
    ? state.connection !== "disconnected"
    : false

  /**
   * We only display a connection button as the right hand
   * element if we're dealing with a WebSocket based API.
   */
  const RightHandElement: JSX.Element = isSocketPlaygroundState(state) ? (
    <ConnectionButton
      onClick={onConnectionButtonClick}
      connection={state.connection}
    >
      {getConnectionButtonText(state.connection)}
    </ConnectionButton>
  ) : (
    <div /> // empty placeholder
  )
  const apisWithDesc: Array<[API, string]> = [
    ["futures", "Futures crypto market"],
    ["historical", "Historical crypto market"],
    ["spot", "Spot crypto market"],
    ["NBA", "NBA"],
    ["NFL", "NFL"]
  ]

  const options = apisWithDesc.map(([api, desc]) => (
    <option key={api}>{desc}</option>
  ))

  // the type assertion (!) here is safe because
  // we're dealing with an ADT (our API type),
  // the compiler is just
  // not smart enough to realize it.
  const selectedApiDescription = apisWithDesc.find(
    ([api, _]) => api === state.selectedAPI
  )![1]

  return (
    <>
      <LogoContainer>
        <a href="/">
          <img alt="Suredbits logo" src={require("./sb-logo.png")} />
        </a>
        <HeaderText>API Playground</HeaderText>
      </LogoContainer>
      <ConnectionContainer>
        <P>Select API:</P>
        <select
          value={selectedApiDescription}
          disabled={isSelectDisabled}
          onChange={onAPIChange}
        >
          {options}
        </select>
        {RightHandElement}
      </ConnectionContainer>
    </>
  )
}
